// ---------------------------------------------------------------------------
// Cookie consent banner -- GDPR/KVKK compliant, no analytics until accepted
// ---------------------------------------------------------------------------

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'nv-cookie-consent';

type ConsentChoice = 'all' | 'essential' | 'dismissed';

function getStoredConsent(): ConsentChoice | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'all' || value === 'essential' || value === 'dismissed') {
      return value;
    }
    return null;
  } catch {
    // localStorage unavailable (e.g. private browsing in some browsers)
    return null;
  }
}

function storeConsent(choice: ConsentChoice): void {
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // silently fail if storage is unavailable
  }
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [hiding, setHiding] = useState(false);

  useEffect(() => {
    const existing = getStoredConsent();
    if (!existing) {
      // Small delay so the banner slides up after the page paints
      const timer = setTimeout(() => setVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismiss = (choice: ConsentChoice) => {
    setHiding(true);
    // Wait for the slide-down animation before removing from DOM
    setTimeout(() => {
      storeConsent(choice);
      setVisible(false);
      setHiding(false);
    }, 300);
  };

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className={`fixed bottom-0 inset-x-0 z-[90] transition-transform duration-300 ease-out ${
        hiding ? 'translate-y-full' : 'animate-slide-up-banner'
      }`}
    >
      <div className="bg-ink-surface border-t border-hairline-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-ink-invert/75 leading-relaxed">
                We use essential cookies to ensure our platform works correctly.
                We also use analytics cookies to understand how you interact with
                our website. You can manage your cookie preferences at any time.{' '}
                <Link
                  to="/cookies"
                  className="text-primary-light hover:text-ink-invert underline underline-offset-2 transition-colors"
                >
                  Cookie Policy
                </Link>
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={() => dismiss('essential')}
                className="px-4 py-2 text-sm font-semibold text-ink-invert border border-hairline-dark rounded-md hover:bg-ink-surface2 hover:text-ink-invert transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 focus-visible:ring-offset-ink-surface"
              >
                Essential Only
              </button>
              <button
                type="button"
                onClick={() => dismiss('all')}
                className="px-4 py-2 text-sm font-semibold text-on-primary bg-primary rounded-md hover:bg-primary-hover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 focus-visible:ring-offset-ink-surface"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
